<template>
  <Select
    ref="selectRef"
    v-bind="attrs"
    v-model:value="state"
    :showSearch="showSearch"
    optionFilterProp="label"
    :class="inMtsApp && showSearch && focusFixed ? 'mts-select-focused-fixed' : ''"
    :placeholder="t(placeholder)"
    :mode="mode"
    :loading="loading"
    :options="getOptions"
    @dropdown-visible-change="handleFetch"
    @change="handleChange"
    @search="debounceSearchFn"
  >
    <template v-for="item in Object.keys($slots)" #[item]="data">
      <slot :name="item" v-bind="data || {}"></slot>
    </template>
    <template v-if="loading" #notFoundContent>
      <span>
        <LoadingOutlined spin class="mr-1" />
        {{ t('common.apiSelectNotFound') }}
      </span>
    </template>
  </Select>
</template>

<script lang="ts" setup>
  import { LoadingOutlined } from '@ant-design/icons-vue';
  import { useDebounceFn } from '@vueuse/core';
  import { Select, type SelectProps } from 'ant-design-vue';
  import { get, isEmpty } from 'lodash-es';
  import {
    computed,
    onMounted,
    PropType,
    ref,
    shallowRef,
    unref,
    useAttrs,
    useTemplateRef,
    watch,
  } from 'vue';
  import type { Fn, Option, Recordable } from '@mts-fe/types';
  import type { SelectValue } from 'ant-design-vue/es/select';
  import { useRuleFormItem } from '@mts-fe/hooks';
  import { isArray, isEqualByStringify, isFunction, propTypes } from '@mts-fe/utils';
  import { t } from '@mts/hooks/web/useI18n';
  import { getGlobalParams } from '@mts/render';
  import { inMtsApp } from '@mts/utils';
  import { PADMode } from '@mts/utils/env';

  interface ApiSearchOption {
    // 待搜索字段名
    searchName?: string;
    // 是否允许空搜索
    emptySearch?: boolean;
    beforeFetch?: (value?: string) => Promise<string | false>;
  }

  defineOptions({ name: 'ApiSelect', inheritAttrs: false });

  const props = defineProps({
    value: { type: [Array, Object, String, Number] as PropType<SelectValue> },
    mode: { type: String as PropType<SelectProps['mode']> },
    api: {
      type: Function as PropType<(arg?: any) => Promise<Option[] | Recordable>>,
      default: null,
    },
    // api params
    params: propTypes.any.def({}),
    // support xxx.xxx.xx
    resultField: propTypes.string.def(''),
    labelField: propTypes.string.def('label'),
    valueField: propTypes.string.def('value'),
    keyField: propTypes.string.def('key'),
    immediate: propTypes.bool.def(true),
    showSearch: propTypes.bool.def(!PADMode),
    alwaysLoad: propTypes.bool.def(false),
    inputAdd: propTypes.bool.def(false),
    focusFixed: propTypes.bool.def(false),
    numberToString: propTypes.bool.def(true),
    useFirstValue: propTypes.bool,
    placeholder: {
      type: String as PropType<SelectProps['placeholder']>,
      default: 'common.chooseText',
    },
    options: {
      type: Array<Option>,
      default: [],
    },
    apiSearch: {
      type: Object as PropType<ApiSearchOption>,
      default: () => null,
    },
    beforeFetch: {
      type: Function as PropType<Fn>,
      default: null,
    },
    afterFetch: {
      type: Function as PropType<Fn>,
      default: null,
    },
  });

  const attrs = useAttrs();
  const emit = defineEmits(['options-change', 'change', 'update:value']);

  const optionsRef = shallowRef<Option[]>([]);

  const loading = ref(false);
  const loadingParams = shallowRef({});
  // 首次是否加载过了
  const isFirstLoaded = ref(false);
  const emitData = shallowRef<Option[]>([]);
  const searchParams = shallowRef<{ [key: string]: unknown }>({});
  const updateSearchParams = (value?: string) => {
    const { apiSearch, showSearch } = props;
    if (!apiSearch || !showSearch) return;

    const { searchName = 'keyword' } = apiSearch;
    searchParams.value = {
      fieldDataCodes:
        value || !state.value ? void 0 : isArray(state.value) ? state.value : [state.value],
      [searchName]: value,
    };
  };

  // Embedded in the form, just use the hook binding to perform form verification
  const [state] = useRuleFormItem(props, 'value', 'change', emitData);

  const inputText = ref('');
  const getOptions = computed(() => {
    const { labelField, valueField, keyField, numberToString, inputAdd } = props;

    let data = unref(optionsRef).reduce((prev, next) => {
      if (next) {
        const value = get(next, valueField);
        prev.push({
          ...next,
          key: get(next, keyField) || `${get(next, labelField)}@${value}`,
          label: get(next, labelField),
          value: numberToString ? `${value}` : value,
        });
      }
      return prev;
    }, [] as Option[]);

    const options = data.length > 0 ? data : props.options;
    if (!inputAdd) return options;

    const text = inputText.value;
    return text && !options.find((i) => i.value === text)
      ? [{ key: text, value: text, label: text }, ...options]
      : options;
  });

  watch(
    () => state.value,
    (value) => {
      if (props.mode === 'multiple' && !value) {
        state.value = [];
      }
      emit('update:value', state.value);
      updateSearchParams();
    },
  );

  watch(
    () => props.params,
    (value, oldValue) => {
      if (isEqualByStringify(value, oldValue)) return;
      requestIdleCallback(fetch);
    },
    { deep: true, immediate: props.immediate },
  );

  if (props.apiSearch) {
    watch(
      () => searchParams.value,
      (value, oldValue) => {
        if (isEmpty(value) || isEqualByStringify(value, oldValue)) return;
        fetch();
      },
      { immediate: props.immediate },
    );
    updateSearchParams();
  }

  async function fetch() {
    let { api, beforeFetch, afterFetch, params, resultField } = props;
    if (
      !api ||
      !isFunction(api) ||
      (loading.value && isEqualByStringify(params, loadingParams.value))
    )
      return;
    optionsRef.value = [];
    try {
      loading.value = true;
      loadingParams.value = params;
      let apiParams = { search: {}, ...params };
      Object.assign(apiParams.search, searchParams.value);
      if (props.showSearch && props.apiSearch && !apiParams.search.limit) {
        apiParams.search.limit = PADMode ? 20 : 100;
      }
      if (beforeFetch && isFunction(beforeFetch)) {
        const globalParams = getGlobalParams();
        const res = await beforeFetch(apiParams, globalParams);
        if (res !== null && typeof res === 'object') {
          apiParams = res;
        }
        if (typeof res === 'boolean' && !res) return;
      }
      let res = await api(apiParams);
      if (afterFetch && isFunction(afterFetch)) {
        res = (await afterFetch(res)) || res;
      }
      isFirstLoaded.value = true;
      if (isArray(res)) {
        optionsRef.value = [...res];
        emitChange();
        return;
      }
      if (resultField) {
        optionsRef.value = get(res, resultField) || [];
      }
      emitChange();
    } catch (error) {
      console.warn(error, params);
      // reset status
      isFirstLoaded.value = false;
    } finally {
      loading.value = false;
      loadingParams.value = {};
    }
  }

  async function handleFetch(visible: boolean) {
    if (visible) {
      if (props.alwaysLoad) {
        await fetch();
      } else if (!props.immediate && !unref(isFirstLoaded)) {
        // 动态搜索查询时，允许控制初始不加载数据
        if (!(!!props.apiSearch && !props.apiSearch.emptySearch)) {
          await fetch();
        } else {
          optionsRef.value = [];
          emitChange();
        }
      }
    }
  }

  async function handleSearch(value) {
    props.inputAdd && (inputText.value = value);
    if (!props.apiSearch) return;

    const { beforeFetch } = props.apiSearch;
    if (beforeFetch && isFunction(beforeFetch)) {
      const res = await beforeFetch(value);
      if (typeof res === 'boolean' && !res) return;
      value = res || value;
    }
    updateSearchParams(value);
  }
  const debounceSearchFn = useDebounceFn(handleSearch, 500);

  function emitChange() {
    emit('options-change', unref(getOptions));

    const first = unref(getOptions)[0];
    if (props.useFirstValue && !state.value && first) {
      state.value = attrs.labelInValue ? first : first.value;
      handleChange(state.value, first);
    }
  }

  function handleChange(_, ...args) {
    emitData.value = args;
  }

  const selectRef = useTemplateRef('selectRef');
  inMtsApp &&
    onMounted(() => {
      if (props.showSearch && props.focusFixed) {
        const width = selectRef.value?.$el?.offsetWidth;
        if (width) {
          selectRef.value.$el.style.maxWidth = width + 'px';
        }
      }
    });
</script>
